import { useEffect, useState } from 'react';
import { Table, Button, DatePicker, Space, Typography, message, Card, Descriptions, Popconfirm } from 'antd';
import { DownloadOutlined, FileAddOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { api } from '../api';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const EUR = n => Number(n).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

export default function InvoicesTab({ storeId }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState([dayjs().subtract(7, 'day'), dayjs().subtract(1, 'day')]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setInvoices(await api.invoices.list(storeId));
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run on storeId change, not on load identity
  useEffect(() => { load(); setPreview(null); }, [storeId]);

  const runPreview = async () => {
    if (!range) return;
    setPreviewLoading(true);
    setPreview(null);
    try {
      const [from, to] = range;
      const result = await api.invoices.preview(storeId, from.format('YYYY-MM-DD'), to.format('YYYY-MM-DD'));
      setPreview(result);
    } catch (err) {
      message.error(err.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const createInvoice = async () => {
    if (!range) return;
    setCreating(true);
    try {
      const [from, to] = range;
      const result = await api.invoices.create({
        store_id: storeId,
        period_from: from.format('YYYY-MM-DD'),
        period_to: to.format('YYYY-MM-DD'),
      });
      message.success(`Rechnung ${result.invoiceNumber} erstellt`);
      setPreview(null);
      load();
    } catch (err) {
      message.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const download = (invoice) => api.invoices.downloadPdf(invoice.id, `${invoice.invoice_number}.pdf`)
    .catch(err => message.error(err.message));

  const columns = [
    { title: 'Nummer', dataIndex: 'invoice_number', key: 'invoice_number' },
    {
      title: 'Zeitraum', key: 'period',
      render: (_, r) => `${dayjs(r.period_from).format('DD.MM.YYYY')} – ${dayjs(r.period_to).format('DD.MM.YYYY')}`,
    },
    { title: 'Bestellungen', dataIndex: 'orders_count', key: 'orders_count', align: 'right' },
    { title: 'Umsatz (brutto)', dataIndex: 'revenue_gross', key: 'revenue_gross', align: 'right', render: EUR },
    { title: 'Provision (brutto)', dataIndex: 'commission_gross', key: 'commission_gross', align: 'right', render: EUR },
    {
      title: 'Auszahlung (brutto)', dataIndex: 'payout_gross', key: 'payout_gross', align: 'right',
      render: v => <Text className={Number(v) < 0 ? 'payout-negative' : undefined} strong>{EUR(v)}</Text>,
    },
    { title: 'Erstellt', dataIndex: 'created_at', key: 'created_at', render: v => dayjs(v).format('DD.MM.YYYY HH:mm') },
    {
      title: '', key: 'actions', width: 60,
      render: (_, r) => <Button size="small" icon={<DownloadOutlined />} onClick={() => download(r)} />,
    },
  ];

  return (
    <>
      <Card size="small" style={{ marginBottom: 16, background: '#f7fafc' }}>
        <Space wrap align="start">
          <RangePicker value={range} onChange={setRange} format="DD.MM.YYYY" allowClear={false} />
          <Button icon={<EyeOutlined />} onClick={runPreview} loading={previewLoading}>
            Vorschau berechnen
          </Button>
          {preview && (
            <Popconfirm
              title="Rechnung erstellen?"
              description="Die Rechnungsnummer wird endgültig vergeben und kann nicht rückgängig gemacht werden."
              onConfirm={createInvoice}
              okText="Erstellen"
              cancelText="Abbrechen"
            >
              <Button type="primary" icon={<FileAddOutlined />} loading={creating}>
                Rechnung erstellen
              </Button>
            </Popconfirm>
          )}
        </Space>

        {preview && (
          <Descriptions size="small" column={4} bordered style={{ marginTop: 16 }}>
            <Descriptions.Item label="Bestellungen">{preview.totals.ordersCount}</Descriptions.Item>
            <Descriptions.Item label="Umsatz">{EUR(preview.totals.revenueGross)}</Descriptions.Item>
            <Descriptions.Item label="bereits kassiert">{EUR(preview.totals.cashCollectedGross)}</Descriptions.Item>
            <Descriptions.Item label="Provision">{EUR(preview.totals.commissionGross)}</Descriptions.Item>
            <Descriptions.Item label="Liefergebühren" span={2}>
              {EUR(preview.totals.deliveryFeeGross)} {preview.storeDetails.selfDelivery ? '(im Umsatz enth.)' : '(an den Lieferservice)'}
            </Descriptions.Item>
            <Descriptions.Item label="Auszahlung" span={2}>
              <Text strong className={preview.totals.payoutGross < 0 ? 'payout-negative' : undefined}>
                {EUR(preview.totals.payoutGross)}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={invoices}
        loading={loading}
        pagination={false}
        size="middle"
      />
    </>
  );
}
